// Mock slide-type catalog for the design demo. Adapted from
// src/constant/slideTypes.js with extra fields (categories, similarTo,
// tagline, useCases, example) for the new picker / marketplace UX.

import type { CategoryId } from './categories'

export type SectionKey = 'quiz' | 'poll' | 'content'

export type SlideType = {
  id: string
  name: string
  desc: string
  icon: string
  accent: string
  sectionKey: SectionKey
  sectionLabel: 'Quiz' | 'Unscored' | 'Content'
  categories: CategoryId[]
  similarTo?: string[]
  isNew?: boolean
  // Slides that only appear in the marketplace by default; users opt them
  // into My Bookmarks via the marketplace. Now informational only —
  // membership is driven by DEFAULT_FAVORITE_IDS below.
  extendedOnly?: boolean
  tagline?: string
  useCases?: string[]
  example?: string
}

export const FEATURED_ID = 'idea-board'

/**
 * Free-plan quotas — drives the per-deck usage indicator next to the
 * My Bookmarks label. Each section type has its own limit; Content is
 * unlimited (null). Cards are tinted by section to make the type and
 * its quota easy to read at a glance.
 */
export const FREE_PLAN_LIMITS: Record<SectionKey, number | null> = {
  quiz: 5,
  poll: 3,
  content: null,
}

/**
 * The 10 slide types that ship in My Bookmarks by default, in popularity
 * order. Anything not in this list lives in the marketplace only until
 * a user explicitly adds it.
 */
export const DEFAULT_FAVORITE_IDS: string[] = [
  'pick-answer',
  'content-slide',
  'word-cloud',
  'poll',
  'open-ended',
  'short-answer',
  'match-pairs',
  'correct-order',
  'spinner-wheel',
  'brainstorm',
]

export const SLIDE_CATALOG: SlideType[] = [
  // ---------- Quiz ----------
  {
    id: 'pick-answer',
    name: 'Pick Answer',
    desc: 'Multiple-choice question scored against the correct answer.',
    icon: 'check_circle',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['short-answer', 'true-false', 'odd-one-out', 'fill-blanks'],
    tagline: 'The classic quiz question — fast, scored, hard to mess up.',
    useCases: [
      'Knowledge checks at the end of a training section',
      'Pop quizzes during a live event',
      'Onboarding fact-recall questions',
    ],
    example: 'Run a 4-option pop quiz at the end of a training session.',
  },
  {
    id: 'short-answer',
    name: 'Short Answer',
    desc: 'Players type the answer; matches earn points.',
    icon: 'edit',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['pick-answer', 'fill-blanks', 'crossword', 'trivia'],
    tagline: 'Type-the-answer rounds — exact, fast, scored.',
    useCases: [
      'Vocabulary or terminology checks',
      'Definitions and recall questions',
      'Fastest-finger-first trivia rounds',
    ],
    example: "\"What's the chemical symbol for sodium?\" — first to type Na scores.",
  },
  {
    id: 'spinner-wheel',
    name: 'Spinner Wheel',
    desc: 'Spin a wheel to randomly pick a name, prompt or prize.',
    icon: 'casino',
    accent: '#FF4081',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['icebreaker', 'quiz'],
    similarTo: ['duck-race', 'random-assignments', 'a-deck-of-cards'],
    tagline: 'Random fairness with a satisfying physics spin.',
    useCases: [
      'Pick a volunteer without playing favourites',
      'Decide who goes first in a discussion',
      'Random raffle / prize draw',
    ],
    example: 'Drop everyone\'s name in, spin to pick the next presenter.',
  },
  {
    id: 'match-pairs',
    name: 'Match Pairs',
    desc: 'Drag and drop to match items into the correct pairs.',
    icon: 'link',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['memory-match', 'correct-order', 'categorise'],
    tagline: 'Drag pairs together — visual recall, fast feedback.',
    useCases: [
      'Match terms with definitions',
      'Pair countries with capitals',
      'Connect causes with effects',
    ],
    example: 'Drag four flags onto their correct continents.',
  },
  {
    id: 'correct-order',
    name: 'Correct Order',
    desc: 'Reorder a list into the right sequence to score.',
    icon: 'sort',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['match-pairs', 'fill-blanks', 'categorise'],
    tagline: 'Sort steps into the right sequence.',
    useCases: [
      'Timelines and historical order',
      'Process and procedure recall',
      'Story-arc reconstruction',
    ],
    example: 'Place the five stages of mitosis in order.',
  },
  {
    id: 'categorise',
    name: 'Categorise',
    desc: 'Players sort items into the correct buckets.',
    icon: 'category',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['match-pairs', 'correct-order', 'odd-one-out'],
    tagline: 'Sort items into the buckets they belong in.',
    useCases: [
      'Group concepts by type',
      'Practice taxonomies and frameworks',
      'Sort customer issues by severity',
    ],
    example: 'Sort 12 fruits into citrus, berry, or stone-fruit.',
  },
  {
    id: 'duck-race',
    name: 'Duck Race',
    desc: 'A playful race where the audience cheers a duck home.',
    icon: 'pets',
    accent: '#FF9068',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['icebreaker', 'quiz'],
    similarTo: ['spinner-wheel', 'random-assignments', 'mood-meter'],
    isNew: true,
    tagline: 'A gentler, sillier alternative to the spinner wheel.',
    useCases: [
      'Light icebreaker at the start of a meeting',
      'Random selection with a bit of suspense',
      'Energiser between heavy content blocks',
    ],
    example: 'Each team picks a duck — first to the finish presents next.',
  },
  {
    id: 'leaderboard',
    name: 'Leaderboard',
    desc: 'Show the top scorers between rounds.',
    icon: 'leaderboard',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    categories: ['quiz'],
    similarTo: ['pick-answer', 'trivia', 'buzzer-round'],
    tagline: 'Show the standings between rounds — keep it competitive.',
    useCases: [
      'Mid-quiz progress reveal',
      'End-of-event prize ceremony',
      'Class-vs-class scoring',
    ],
    example: 'Reveal the top 5 after a 10-question quiz.',
  },

  // ---------- Polls / Q&A (Unscored) ----------
  {
    id: 'poll',
    name: 'Poll',
    desc: 'Multi-choice opinion poll — fastest way to read the room.',
    icon: 'bar_chart',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['polls', 'discussion'],
    similarTo: ['rating-scale', 'sentiment-check', 'priority-vote'],
    tagline: 'The fastest way to find out what people think.',
    useCases: [
      'Read the room before a discussion',
      'Validate a hypothesis with the audience',
      'Compare opinions across teams',
    ],
    example: '“Which option should we ship first?” with live bar-chart results.',
  },
  {
    id: 'open-ended',
    name: 'Open Ended',
    desc: 'Free-text replies streamed live as participants submit.',
    icon: 'chat_bubble',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['polls', 'discussion', 'psych-safety'],
    similarTo: ['word-cloud', 'qna', 'confessions', 'walking-question'],
    tagline: 'Free-text replies, streamed live.',
    useCases: [
      'Soak up qualitative input fast',
      'Anonymous feedback rounds',
      'Stand-up status check',
    ],
    example: "\"What's one thing slowing you down this week?\"",
  },
  {
    id: 'word-cloud',
    name: 'Word Cloud',
    desc: 'Audience words form a live cloud — bigger means more popular.',
    icon: 'cloud',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['icebreaker', 'polls', 'discussion'],
    similarTo: ['open-ended', 'brainstorm', 'mood-meter'],
    tagline: 'Visual gut-check on what\'s top-of-mind for the room.',
    useCases: [
      'Icebreakers ("What word describes your week?")',
      'Sentiment snapshot before a topic',
      'Live keyword brainstorming',
    ],
    example: '“In one word, what excites you about this quarter?”',
  },
  {
    id: 'brainstorm',
    name: 'Brainstorm',
    desc: 'Audience submits ideas, then upvotes the best ones.',
    icon: 'lightbulb',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['brainstorm', 'discussion'],
    similarTo: ['idea-board', 'sticky-notes', 'mind-map', 'priority-vote'],
    tagline: 'Generate, then sort. Best ideas float to the top.',
    useCases: [
      'Roadmap input from a wide audience',
      'Crowd-sourced retrospectives',
      'Ideation when you need a clear winner',
    ],
    example: '“What should we improve about onboarding?” — vote up the best.',
  },
  {
    id: 'idea-board',
    name: 'Idea Board',
    desc: 'A flexible sticky-note canvas for collaborative ideation.',
    icon: 'dashboard',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['brainstorm', 'discussion'],
    similarTo: ['brainstorm', 'sticky-notes', 'whiteboard', 'mind-map'],
    isNew: true,
    tagline: 'A flexible canvas of sticky notes the room builds together.',
    useCases: [
      'Affinity-mapping retros',
      'Open-ended ideation',
      'Working-session whiteboards',
    ],
    example: 'Cluster sticky notes around themes during a project kickoff.',
  },
  {
    id: 'pin-on-image',
    name: 'Pin on Image',
    desc: 'Audience drops pins on an image to indicate a position.',
    icon: 'pin_drop',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['polls', 'discussion'],
    similarTo: ['heatmap-vote', 'image', 'image-pair'],
    tagline: 'Drop pins on an image — visual answers and heat-maps.',
    useCases: [
      "\"Where's your office?\" map activity",
      'Click-the-bug bug bounty rounds',
      'Pinpoint feedback on a UI screenshot',
    ],
    example: 'Audience pins where they live on a world map.',
  },
  {
    id: 'ranking',
    name: 'Ranking',
    desc: 'Ask the audience to rank a list of options.',
    icon: 'reorder',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['polls'],
    similarTo: ['priority-vote', 'pros-cons', 'rating-scale'],
    tagline: 'Force a ranked order — surface clear priorities.',
    useCases: [
      'Feature prioritisation',
      'Movie / song bracket-style polls',
      'Triage exercises',
    ],
    example: 'Rank these five product ideas from most to least valuable.',
  },
  {
    id: 'rating-scale',
    name: 'Rating Scale',
    desc: 'Collect ratings on a 1-to-N scale, average shown live.',
    icon: 'star_rate',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['polls', 'psych-safety'],
    similarTo: ['poll', 'sentiment-check', 'mood-meter'],
    tagline: 'A 1-to-N pulse on any topic — average shown live.',
    useCases: [
      'Confidence checks before a decision',
      'Quick session feedback',
      'Comparing across items on the same scale',
    ],
    example: '"Rate your confidence in the rollout plan, 1-10."',
  },
  {
    id: 'qna',
    name: 'Q&A',
    desc: 'Audience submits questions, others upvote.',
    icon: 'help',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    categories: ['discussion', 'psych-safety'],
    similarTo: ['town-hall', 'open-ended', 'confessions'],
    tagline: 'Audience-driven Q&A — best questions float to the top.',
    useCases: [
      'All-hands and town halls',
      'Speaker Q&A with crowd voting',
      'Anonymous question time',
    ],
    example: 'Run a 15-min Q&A — audience upvotes the questions you answer.',
  },

  // ---------- Content ----------
  {
    id: 'content-slide',
    name: 'Content',
    desc: 'A flexible canvas for free-form text, images and shapes.',
    icon: 'auto_awesome_mosaic',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['heading', 'list', 'quote', 'cta-banner'],
    tagline: 'Free-form canvas for text, images, and shapes.',
    useCases: [
      'Section title slides',
      'Branded callouts',
      'Hand-built infographics',
    ],
    example: 'Compose an opening slide with a hero image and quote.',
  },
  {
    id: 'heading',
    name: 'Heading',
    desc: 'A bold section divider to introduce a new chapter.',
    icon: 'title',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['content-slide', 'quote', 'cta-banner'],
    tagline: 'A bold section break that resets attention.',
    useCases: [
      'Chapter dividers in long decks',
      'Topic transitions',
      'Welcome and closing slides',
    ],
    example: 'Drop a Heading slide between the agenda and first topic.',
  },
  {
    id: 'list',
    name: 'List',
    desc: 'A clean list of bullet points or numbered items.',
    icon: 'format_list_bulleted',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['heading', 'content-slide', 'table'],
    tagline: 'Clean bullet or numbered lists — readable from the back row.',
    useCases: [
      'Agenda slides',
      'Step-by-step instructions',
      'Key takeaways',
    ],
    example: 'A numbered list of the 5 product principles.',
  },
  {
    id: 'image',
    name: 'Image',
    desc: 'A full-bleed image slide.',
    icon: 'image',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['image-pair', 'pin-on-image', 'whiteboard'],
    tagline: 'A full-bleed image — let visuals do the talking.',
    useCases: [
      'Photo of the team or venue',
      'Charts and diagrams as images',
      'Inspirational moments',
    ],
    example: "Open with a photo from last year's offsite.",
  },
  {
    id: 'qr-code',
    name: 'QR Code',
    desc: 'Show a QR code that links anywhere — joining included.',
    icon: 'qr_code_2',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['cta-banner', 'embed', 'document'],
    tagline: 'A QR code that links the audience anywhere.',
    useCases: [
      'Direct people to a survey or sign-up',
      'Share files and resources',
      'Take attendance via a simple link',
    ],
    example: "Scan to download today's slide deck.",
  },
  {
    id: 'youtube',
    name: 'YouTube',
    desc: 'Embed and play a YouTube video right inside the deck.',
    icon: 'play_circle',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['video-host', 'audio-clip', 'embed'],
    tagline: 'Embed a YouTube clip without leaving the deck.',
    useCases: [
      'Show a customer testimonial',
      'Play a short ad or trailer',
      'Recap a clip with audience reaction',
    ],
    example: 'Play a 60-second product demo at the start of the talk.',
  },
  {
    id: 'embed',
    name: 'Embed',
    desc: 'Embed any iframe-friendly URL.',
    icon: 'language',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    categories: ['content'],
    similarTo: ['youtube', 'document', 'qr-code'],
    tagline: 'Embed any iframe-friendly URL.',
    useCases: [
      'Live dashboards and metrics',
      'Documents and spreadsheets',
      'Interactive web demos',
    ],
    example: 'Embed a live Looker dashboard for the quarterly review.',
  },
  // ---------- Quiz expansion ----------
  {
    id: 'true-false',
    name: 'True or False',
    desc: 'Swipe-style yes/no statements scored for speed and accuracy.',
    icon: 'swipe',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['pick-answer', 'sentiment-check', 'odd-one-out', 'fill-blanks'],
    tagline: 'A speed round of swipes — perfect for warm-ups.',
  },
  {
    id: 'hot-seat',
    name: 'Hot Seat',
    desc: 'One player answers under pressure while the room watches.',
    icon: 'whatshot',
    accent: '#FF4081',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['buzzer-round', 'walking-question', 'two-truths-lie'],
    tagline: 'One player on the spot — the room watches and roots.',
    useCases: [
      'Trivia tie-breakers',
      'Energiser challenge',
      'Sales-floor product knowledge spot-checks',
    ],
    example: 'Pick a name; they have 30 seconds to answer 5 questions.',
  },
  {
    id: 'buzzer-round',
    name: 'Buzzer Round',
    desc: 'First-to-buzz wins the chance to answer — high energy.',
    icon: 'notifications_active',
    accent: '#FF4081',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['hot-seat', 'trivia', 'leaderboard'],
    tagline: 'First to buzz wins the chance to answer — high energy.',
    useCases: [
      'Quiz-show finales',
      'Tie-breaker rounds',
      'Live-event grand prize',
    ],
    example: 'Ask a tough question; whoever buzzes first answers it.',
  },
  {
    id: 'crossword',
    name: 'Crossword',
    desc: 'Solve a small crossword grid against the clock.',
    icon: 'apps',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['fill-blanks', 'memory-match', 'short-answer'],
    tagline: 'A small crossword grid against the clock.',
    useCases: [
      'Vocabulary review',
      'Subject-matter recall',
      'Calm-but-engaging warm-down',
    ],
    example: 'Solve a 5x5 crossword on key product terms.',
  },
  {
    id: 'fill-blanks',
    name: 'Fill in the Blanks',
    desc: 'A sentence with missing words for the audience to complete.',
    icon: 'edit_note',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['short-answer', 'crossword', 'pick-answer'],
    tagline: 'Cloze-test sentences — fill in the missing words.',
    useCases: [
      'Language learning drills',
      'Definition recall',
      'Complete-the-policy exercises',
    ],
    example: '"The capital of ____ is Lisbon." — type the country.',
  },
  {
    id: 'memory-match',
    name: 'Memory Match',
    desc: 'Flip cards two at a time — match pairs to win.',
    icon: 'view_module',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['match-pairs', 'crossword', 'pictionary'],
    tagline: 'Flip cards two at a time — match pairs to win.',
    useCases: [
      'Visual association practice',
      'Brand or logo recognition',
      'Light-touch warm-up',
    ],
    example: 'Match logos to brand names by flipping the right pairs.',
  },
  {
    id: 'trivia',
    name: 'Trivia Pursuit',
    desc: 'Round-robin trivia across categories — Jeopardy-style.',
    icon: 'extension',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['pick-answer', 'buzzer-round', 'leaderboard', 'short-answer'],
    tagline: 'Round-robin trivia across topics — Jeopardy-style.',
    useCases: [
      'Pub-quiz nights',
      'Long workshop energiser',
      'Team-building tournaments',
    ],
    example: 'Pick a category and a value — answer for points.',
  },
  {
    id: 'random-assignments',
    name: 'Random Assignments',
    desc: 'Pick a box, open it, reveal a surprise prompt.',
    icon: 'inventory_2',
    accent: '#FF9068',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['spinner-wheel', 'a-deck-of-cards', 'duck-race'],
    isNew: true,
    tagline: 'A box, a click, a surprise inside.',
    useCases: [
      'Random group assignments',
      'Mystery prompts',
      'Energiser between sessions',
    ],
    example: 'Each person clicks a box; their next break-out topic is revealed.',
  },
  {
    id: 'a-deck-of-cards',
    name: 'A Deck of Cards',
    desc: 'Shuffle a deck and let participants draw a card.',
    icon: 'style',
    accent: '#FF9068',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['spinner-wheel', 'random-assignments', 'walking-question'],
    tagline: 'Shuffle a deck — let the audience draw a card.',
    useCases: [
      'Random discussion prompts',
      'Card-based icebreakers',
      'Light-touch decision-making',
    ],
    example: "Draw a card; that's your next question to discuss.",
  },
  {
    id: 'sneak-a-peek',
    name: 'Sneak a Peek',
    desc: 'Glimpse an image briefly, then guess what it shows.',
    icon: 'preview',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['memory-match', 'pictionary', 'odd-one-out'],
    tagline: 'Glimpse an image briefly, then guess what it shows.',
    useCases: [
      'Observation-skill drills',
      'Brand-recognition quizzes',
      'Mindfulness warm-ups',
    ],
    example: 'Show a logo for 1 second — players guess the brand.',
  },
  {
    id: 'walking-question',
    name: 'Walking Question',
    desc: 'A question travels round-robin around the room.',
    icon: 'directions_walk',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'discussion'],
    similarTo: ['hot-seat', 'two-truths-lie', 'open-ended'],
    tagline: 'A question travels the room, round-robin style.',
    useCases: [
      'Each-person-says-one icebreakers',
      'Project handoff status',
      'Sentiment go-rounds',
    ],
    example: "Each person, in turn, names one thing they're working on.",
  },
  {
    id: 'pictionary',
    name: 'Draw',
    desc: 'Players sketch the answer; the room guesses what it is.',
    icon: 'brush',
    accent: '#FF4081',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz', 'icebreaker'],
    similarTo: ['memory-match', 'sneak-a-peek', 'whiteboard'],
    isNew: true,
    tagline: 'Sketch the answer; the room guesses what it is.',
    useCases: [
      'Creative warm-ups',
      'Team challenges',
      'Cross-functional ice-breakers',
    ],
    example: 'Player draws an animal — others guess in chat.',
  },
  {
    id: 'odd-one-out',
    name: 'Odd One Out',
    desc: 'Spot the option that doesn\'t belong.',
    icon: 'filter_alt',
    accent: '#6A1EBB',
    sectionKey: 'quiz',
    sectionLabel: 'Quiz',
    extendedOnly: true,
    categories: ['quiz'],
    similarTo: ['categorise', 'true-false', 'pick-answer'],
    tagline: "Spot the option that doesn't belong.",
    useCases: [
      'Pattern recognition',
      'Concept differentiation',
      'Quick deduction rounds',
    ],
    example: '"Apple, Pear, Orange, Carrot" — which one is the odd one out?',
  },
  // ---------- Unscored expansion ----------
  {
    id: 'two-truths-lie',
    name: 'Two Truths, One Lie',
    desc: 'Three statements about you — the room guesses the lie.',
    icon: 'psychology',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['icebreaker', 'discussion'],
    similarTo: ['confessions', 'walking-question', 'mood-meter'],
    isNew: true,
    tagline: 'Three statements; the room guesses the lie.',
    useCases: [
      'First-meeting icebreakers',
      'Building team rapport',
      'Light-hearted onboarding',
    ],
    example: "\"I've climbed Kilimanjaro, eaten octopus, and met a celebrity.\" — which is the lie?",
  },
  {
    id: 'mood-meter',
    name: 'Mood Meter',
    desc: 'Pick an emoji that captures how you feel right now.',
    icon: 'mood',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['icebreaker', 'psych-safety'],
    similarTo: ['sentiment-check', 'rating-scale', 'word-cloud'],
    tagline: 'Pick an emoji that captures how you feel.',
    useCases: [
      'Start-of-meeting check-ins',
      'End-of-week pulse',
      'Quick wellness signals',
    ],
    example: 'Drop an emoji that sums up your week so far.',
  },
  {
    id: 'sentiment-check',
    name: 'Sentiment Check',
    desc: 'Quick thumbs up / down / sideways pulse on a topic.',
    icon: 'thumbs_up_down',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['polls', 'psych-safety'],
    similarTo: ['mood-meter', 'rating-scale', 'true-false'],
    tagline: 'Thumbs up / down / sideways pulse on a topic.',
    useCases: [
      'Decision-making temperature',
      'Quick consent checks',
      'Reaction tracker during talks',
    ],
    example: '"We should ship next week — thumbs up if you agree."',
  },
  {
    id: 'confessions',
    name: 'Confessions',
    desc: 'Anonymous prompt the room responds to safely.',
    icon: 'lock_person',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['psych-safety', 'discussion'],
    similarTo: ['two-truths-lie', 'open-ended', 'qna'],
    tagline: 'Anonymous prompt — let the room respond safely.',
    useCases: [
      'Surfacing taboo topics',
      'Vulnerable discussions',
      'Honest feedback channels',
    ],
    example: "\"What's one thing you're afraid to bring up with the team?\"",
  },
  {
    id: 'heatmap-vote',
    name: 'Heatmap Vote',
    desc: 'Drop dots on a 2D matrix — visualise group sentiment.',
    icon: 'grid_view',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['polls', 'discussion'],
    similarTo: ['pin-on-image', 'priority-vote', 'pros-cons'],
    tagline: "Drop dots on a 2D matrix — visualise the group's view.",
    useCases: [
      'Effort-vs-impact prioritisation',
      'Risk-reward mapping',
      'Skill-confidence matrix',
    ],
    example: 'Plot each idea on the impact-vs-effort grid.',
  },
  {
    id: 'pros-cons',
    name: 'Pros / Cons',
    desc: 'Side-by-side board for weighing two options.',
    icon: 'balance',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['discussion', 'brainstorm'],
    similarTo: ['swot', 'priority-vote', 'ranking'],
    tagline: 'Side-by-side board for weighing two options.',
    useCases: [
      'Tooling and vendor comparisons',
      'Build-vs-buy debates',
      'Strategic option weighing',
    ],
    example: '"React Native vs native iOS" — pros on the left, cons on the right.',
  },
  {
    id: 'swot',
    name: 'SWOT',
    desc: 'Strengths, Weaknesses, Opportunities, Threats — collaboratively.',
    icon: 'view_quilt',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['discussion', 'brainstorm'],
    similarTo: ['pros-cons', 'mind-map', 'sticky-notes'],
    tagline: 'Strengths, Weaknesses, Opportunities, Threats — collaboratively.',
    useCases: [
      'Strategic planning sessions',
      'Competitive-landscape mapping',
      'Annual review exercises',
    ],
    example: 'Run a SWOT on the new product line in 15 minutes.',
  },
  {
    id: 'sticky-notes',
    name: 'Sticky Notes',
    desc: 'Free-form sticky note canvas for brainstorming together.',
    icon: 'sticky_note_2',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['brainstorm', 'discussion'],
    similarTo: ['idea-board', 'brainstorm', 'whiteboard', 'mind-map'],
    tagline: 'Free-form sticky-note canvas for collaborative thinking.',
    useCases: [
      'Workshop brainstorms',
      'Retrospective canvases',
      'Mind-mapping sessions',
    ],
    example: 'Cluster sticky notes around the question "What should we keep doing?"',
  },
  {
    id: 'mind-map',
    name: 'Mind Map',
    desc: 'Branch out from a central idea collaboratively.',
    icon: 'hub',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['brainstorm'],
    similarTo: ['sticky-notes', 'idea-board', 'brainstorm'],
    tagline: 'Branch out from a central idea, together.',
    useCases: [
      'Systems-thinking sessions',
      'Problem decomposition',
      'Note-taking during research',
    ],
    example: 'Map out everything the team knows about a customer segment.',
  },
  {
    id: 'fishbowl',
    name: 'Fishbowl',
    desc: 'Inner-circle discussion, outer circle observes and reacts.',
    icon: 'panorama_fish_eye',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['discussion'],
    similarTo: ['town-hall', 'walking-question', 'qna'],
    tagline: 'Inner circle discusses; outer circle observes and reacts.',
    useCases: [
      'Roleplay-driven retrospectives',
      'Diversity and inclusion conversations',
      'Cross-team alignment',
    ],
    example: 'Five people debate while the rest tag in or react.',
  },
  {
    id: 'town-hall',
    name: 'Town Hall',
    desc: 'Audience asks, you answer live — questions queued and upvoted.',
    icon: 'forum',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['discussion', 'polls'],
    similarTo: ['qna', 'fishbowl', 'open-ended'],
    tagline: 'Audience asks; you answer live.',
    useCases: [
      'All-hands and AMAs',
      'Customer-advisory sessions',
      'Open Q&A with leadership',
    ],
    example: 'Run a 30-minute Q&A on the new strategy.',
  },
  {
    id: 'priority-vote',
    name: 'Priority Vote',
    desc: 'Allocate dots across options to surface group priorities.',
    icon: 'low_priority',
    accent: '#20E8B5',
    sectionKey: 'poll',
    sectionLabel: 'Unscored',
    extendedOnly: true,
    categories: ['polls', 'discussion'],
    similarTo: ['ranking', 'pros-cons', 'heatmap-vote'],
    tagline: 'Allocate dots across options — surface what matters.',
    useCases: [
      'Roadmap planning',
      'Backlog grooming',
      'Resource-allocation debates',
    ],
    example: 'Each person gets 5 dots — vote for the top initiatives.',
  },
  // ---------- Content expansion ----------
  {
    id: 'document',
    name: 'Document',
    desc: 'Upload a PDF or doc as a slide.',
    icon: 'description',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['table', 'embed', 'list'],
    tagline: 'Drop in a PDF or doc as a slide.',
    useCases: [
      'Whitepapers in a deck',
      'Reference material',
      'Long-form reading interludes',
    ],
    example: 'Embed the quarterly report as a flipbook slide.',
  },
  {
    id: 'quote',
    name: 'Quote',
    desc: 'A pull quote with attribution, large and centred.',
    icon: 'format_quote',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['heading', 'content-slide', 'cta-banner'],
    tagline: 'A pull quote with attribution — large and centred.',
    useCases: [
      'Customer testimonials',
      'Inspirational openers',
      'Mission and values reinforcement',
    ],
    example: 'Lead with a customer quote about why they switched.',
  },
  {
    id: 'video-host',
    name: 'Video',
    desc: 'Upload an MP4 to play full-bleed within your deck.',
    icon: 'movie',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['youtube', 'audio-clip', 'image-pair'],
    tagline: 'Upload an MP4 — full-bleed playback in the deck.',
    useCases: [
      'Self-recorded demos',
      'Product-launch hype reels',
      'Training-content snippets',
    ],
    example: 'Show a 30-second demo recording of the new feature.',
  },
  {
    id: 'code-block',
    name: 'Code Block',
    desc: 'Syntax-highlighted code snippet with line numbers.',
    icon: 'code',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['document', 'embed', 'diagram'],
    tagline: 'Syntax-highlighted code with line numbers.',
    useCases: [
      'Tech-talk walkthroughs',
      'API examples',
      'Live-coding setup',
    ],
    example: 'Show the 10 lines that wire up auth in the new SDK.',
  },
  {
    id: 'table',
    name: 'Table',
    desc: 'Editable rows and columns for tabular content.',
    icon: 'table_view',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['list', 'document', 'diagram'],
    tagline: 'Editable rows and columns — tabular content done right.',
    useCases: [
      'Comparisons and matrices',
      'Schedules and timetables',
      'Pricing tables',
    ],
    example: 'Show the feature comparison across plans.',
  },
  {
    id: 'diagram',
    name: 'Diagram',
    desc: 'Draw flowcharts and shapes with auto-routing arrows.',
    icon: 'schema',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['whiteboard', 'mind-map', 'timeline'],
    tagline: 'Flowcharts and shapes with auto-routing arrows.',
    useCases: [
      'System architectures',
      'Process flows',
      'Decision trees',
    ],
    example: 'Diagram the request flow from client to database.',
  },
  {
    id: 'timeline',
    name: 'Timeline',
    desc: 'Horizontal milestones for telling a sequence story.',
    icon: 'timeline',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['diagram', 'list', 'document'],
    tagline: 'Horizontal milestones — tell a sequence story.',
    useCases: [
      'Product roadmaps',
      'Project schedules',
      'Company history',
    ],
    example: 'Show the rollout schedule across the next 4 quarters.',
  },
  {
    id: 'countdown-timer',
    name: 'Countdown Timer',
    desc: 'A big visible timer for breaks, exercises, or pacing.',
    icon: 'timer',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['cta-banner', 'mood-meter', 'spinner-wheel'],
    tagline: 'A big timer for breaks, exercises, or pacing.',
    useCases: [
      'Break and lunch timers',
      'Exercise time-boxing',
      'Energiser activities',
    ],
    example: 'Run a 10-minute breakout with a visible countdown.',
  },
  {
    id: 'whiteboard',
    name: 'Whiteboard',
    desc: 'A blank canvas for live drawing and notes.',
    icon: 'draw',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content', 'brainstorm'],
    similarTo: ['idea-board', 'sticky-notes', 'mind-map', 'diagram'],
    tagline: 'A blank canvas for live drawing and notes.',
    useCases: [
      'Live diagramming',
      'Free-form brainstorming',
      'Visual problem-solving',
    ],
    example: 'Sketch the new architecture together as you talk.',
  },
  {
    id: 'image-pair',
    name: 'Compare Images',
    desc: 'Two images side-by-side with an optional slider.',
    icon: 'compare',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['image', 'pin-on-image', 'video-host'],
    tagline: 'Two images side-by-side — compare with a slider.',
    useCases: [
      'Before/after reveals',
      'Old vs new design comparisons',
      'Pre/post product screenshots',
    ],
    example: 'Slide between the old and new homepage to show the redesign.',
  },
  {
    id: 'audio-clip',
    name: 'Audio Clip',
    desc: 'Embed an audio track that plays when the slide is shown.',
    icon: 'graphic_eq',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['video-host', 'youtube', 'embed'],
    tagline: 'Embed an audio track that plays on the slide.',
    useCases: [
      'Music for energy breaks',
      'Customer-call snippets',
      'Sound-design demos',
    ],
    example: 'Play a 30-second clip of a customer interview.',
  },
  {
    id: 'cta-banner',
    name: 'Call-to-Action',
    desc: 'A bold banner with a primary action button (URL or QR).',
    icon: 'campaign',
    accent: '#20E8B5',
    sectionKey: 'content',
    sectionLabel: 'Content',
    extendedOnly: true,
    categories: ['content'],
    similarTo: ['qr-code', 'heading', 'quote'],
    tagline: 'A bold banner with a primary action button.',
    useCases: [
      'Drive sign-ups in real time',
      'End-of-talk next steps',
      'Promote events and drops',
    ],
    example: 'Close with "Scan to join the beta" + a QR code.',
  },
]

export function findSlide(id: string): SlideType | undefined {
  return SLIDE_CATALOG.find((s) => s.id === id)
}
